import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './VehicleSelection.css';
import { apiRequest, parseResponse } from './utils/api';
import { useLanguage } from './i18n/LanguageContext';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const mapDot = (color) => L.divIcon({
  className: 'ride-map-dot',
  html: `<span style="background:${color}"></span>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

const unwrapCarList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object') {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.cars)) return data.cars;
    if (Array.isArray(data.vehicles)) return data.vehicles;
    if (Array.isArray(data.result)) return data.result;
    if (data.data && Array.isArray(data.data.cars)) return data.data.cars;
    if (data.data && Array.isArray(data.data.vehicles)) return data.data.vehicles;
    if (data.data && Array.isArray(data.data.data)) return data.data.data;
  }
  return data ? [data] : [];
};

const packageListOf = (car) => {
  if (Array.isArray(car?.packages)) return car.packages;
  if (Array.isArray(car?.clientPackages)) return car.clientPackages;
  if (Array.isArray(car?.packageList)) return car.packageList;
  return [];
};

const resolvePackageId = (pkg) => {
  if (!pkg || typeof pkg !== 'object') {
    return null;
  }
  const value = pkg.clientPackageId
    ?? pkg.client_package_id
    ?? pkg.packageId
    ?? pkg.package_id
    ?? pkg.clientPackage?.id
    ?? pkg.clientPackage?.clientPackageId
    ?? pkg.package?.id
    ?? pkg.package?.clientPackageId
    ?? pkg.id
    ?? null;
  return value === '' ? null : value;
};

// Function to normalize API response - handle both grouped and flat formats
const normalizeCarData = (payload) => {
  const data = unwrapCarList(payload);
  // If data is already grouped by car (has packages array), use it directly
  if (Array.isArray(data) && data.length > 0 && packageListOf(data[0]).length > 0) {
    return data.map((car, index) => ({
      carId: car.carId || car.id || `${car.carMakeName}_${car.carModelName}_${index}` || `car_${index}`,
      carMakeName: car.carMakeName || '',
      carModelName: car.carModelName || '',
      carImage: car.carImage || car.imageUrl || null,
      seatingCapacity: car.seatingCapacity || 0,
      luggageCapacity: car.luggageCapacity || 0,
      packages: packageListOf(car)
    }));
  }
  
  // If data is flat (old format), group it
  const carMap = new Map();
  const packagesArray = Array.isArray(data) ? data : [data];
  
  packagesArray.forEach((pkg) => {
    const carId = pkg.carId || 
                  (pkg.carMakeName && pkg.carModelName ? `${pkg.carMakeName}_${pkg.carModelName}` : null) ||
                  pkg.vehicleId ||
                  'unknown';
    
    if (!carMap.has(carId)) {
      carMap.set(carId, {
        carId: carId,
        carMakeName: pkg.carMakeName || '',
        carModelName: pkg.carModelName || '',
        carImage: pkg.carImage || pkg.imageUrl || null,
        seatingCapacity: pkg.seatingCapacity || 0,
        luggageCapacity: pkg.luggageCapacity || 0,
        packages: []
      });
    }
    
    carMap.get(carId).packages.push(pkg);
  });

  return Array.from(carMap.values());
};

const VehicleSelection = ({ tripData, onBack, onSelectVehicle }) => {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [expandedCars, setExpandedCars] = useState(new Set());
  const [isWide, setIsWide] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const onChange = (event) => setIsWide(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get coordinates from trip data
        const startLat = tripData?.pickupLocation?.latitude || null;
        const startLng = tripData?.pickupLocation?.longitude || null;
        const endLat = tripData?.dropLocation?.latitude || null;
        const endLng = tripData?.dropLocation?.longitude || null;

        if (!startLat || !startLng || !endLat || !endLng) {
          throw new Error('Missing location coordinates');
        }

        // Get hotelId from localStorage
        const selectedHotel = JSON.parse(localStorage.getItem('selectedHotel') || '{}');
        const hotelId = selectedHotel?.id || null;

        // Prepare API request with coordinates and hotelId
        const apiData = {
          startLat: startLat,
          startLng: startLng,
          endLat: endLat,
          endLng: endLng
        };

        // Add hotelId if available
        if (hotelId) {
          apiData.hotelId = hotelId;
        }

        // Call API to get available vehicles
        const response = await apiRequest('/car-search', {
          method: 'POST',
          body: JSON.stringify(apiData)
        });

        if (!response.ok) {
          throw new Error('Failed to fetch vehicles');
        }

        const data = await parseResponse(response);
        
        // Normalize the data - handle both grouped (new) and flat (old) formats
        const normalizedCars = normalizeCarData(data);

        if (normalizedCars.length > 0) {
          setVehicles(normalizedCars);
          // Expand first car by default
          if (normalizedCars.length > 0) {
            setExpandedCars(new Set([normalizedCars[0].carId]));
          }
        } else {
          setError('No vehicles available for this trip');
        }
      } catch (err) {
        console.error('Error fetching vehicles:', err);
        setError('Unable to load vehicles. Please try again.');
        // Set default vehicles if API fails
        setVehicles([
          { carId: 'premium-sedan', carMakeName: 'Premium sedan', seatingCapacity: 4, luggageCapacity: 2, packages: [{ packageName: 'Standard', seatingCapacity: 4, luggageCapacity: 2, estimatedTotalCost: 1150 }] },
          { carId: 'executive-suv', carMakeName: 'Executive SUV', seatingCapacity: 6, luggageCapacity: 4, packages: [{ packageName: 'Standard', seatingCapacity: 6, luggageCapacity: 4, estimatedTotalCost: 1850 }] },
          { carId: 'luxury-black', carMakeName: 'Luxury black', seatingCapacity: 3, luggageCapacity: 2, packages: [{ packageName: 'Standard', seatingCapacity: 3, luggageCapacity: 2, estimatedTotalCost: 2450 }] },
          { carId: 'group-sprinter', carMakeName: 'Group sprinter', seatingCapacity: 12, luggageCapacity: 10, packages: [{ packageName: 'Standard', seatingCapacity: 12, luggageCapacity: 10, estimatedTotalCost: 4200 }] }
        ]);
      } finally {
        setLoading(false);
      }
    };

    if (tripData) {
      fetchVehicles();
    }
  }, [tripData]);

  const handleVehicleSelect = (packageData) => {
    setSelectedVehicle(packageData);
    // Don't call onSelectVehicle immediately, wait for Continue button
  };

  const toggleCarExpansion = (carId) => {
    setExpandedCars(prev => {
      const newSet = new Set(prev);
      if (newSet.has(carId)) {
        newSet.delete(carId);
      } else {
        newSet.add(carId);
      }
      return newSet;
    });
  };

  const handleContinue = () => {
    if (!selectedVehicle) return;
    
    // Navigate to booking summary instead of creating booking directly
    onSelectVehicle({
      ...tripData,
      selectedVehicle: selectedVehicle
    });
  };

  const pickupLat = tripData?.pickupLocation?.latitude;
  const pickupLng = tripData?.pickupLocation?.longitude;
  const dropLat = tripData?.dropLocation?.latitude;
  const dropLng = tripData?.dropLocation?.longitude;
  const hasRoute = [pickupLat, pickupLng, dropLat, dropLng].every((value) => typeof value === 'number');

  return (
    <div className={`vehicle-selection-screen ${isWide ? 'is-wide' : ''}`}>
      {isWide && hasRoute && (
        <div className="vehicle-map">
          <MapContainer
            center={[(pickupLat + dropLat) / 2, (pickupLng + dropLng) / 2]}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            zoomControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Polyline positions={[[pickupLat, pickupLng], [dropLat, dropLng]]} pathOptions={{ color: '#764CA2', weight: 4 }} />
            <Marker position={[pickupLat, pickupLng]} icon={mapDot('#4CAF50')}>
              <Popup>{tripData?.pickupPoint}</Popup>
            </Marker>
            <Marker position={[dropLat, dropLng]} icon={mapDot('#FF9800')}>
              <Popup>{tripData?.dropDestination}</Popup>
            </Marker>
          </MapContainer>
        </div>
      )}
      <div className="vehicle-selection-content">
        <header className="vehicle-header">
          <button className="back-button" onClick={onBack} aria-label="Go back">
            <img
              src={process.env.PUBLIC_URL + '/images/back_button.svg'}
              alt="Back"
            />
          </button>
          <div className="vehicle-heading">
            <h1 className="vehicle-title">{t('Select your ride')}</h1>
            <p className="vehicle-subtitle">{t('Choose from our premium fleet.')}</p>
          </div>
        </header>

        {isWide && (
          <aside className="ride-summary-card">
            <div className="ride-stop">
              <span className="ride-dot ride-dot-pickup" />
              <div>
                <span className="ride-kicker">{t('PICK UP')}</span>
                <strong>{tripData?.pickupPoint}</strong>
              </div>
            </div>
            <div className="ride-stop">
              <span className="ride-dot ride-dot-drop" />
              <div>
                <span className="ride-kicker">{t('DROP OFF')}</span>
                <strong>{tripData?.dropDestination}</strong>
              </div>
            </div>
            <div className="ride-summary-grid">
              <div className="ride-summary-field">
                <span>{t('GUESTS')}</span>
                <strong>{tripData?.guests}</strong>
              </div>
              <div className="ride-summary-field">
                <span>{t('BAGS')}</span>
                <strong>{tripData?.bags}</strong>
              </div>
              <div className="ride-summary-field">
                <span>{t('DATE')}</span>
                <strong>{tripData?.formattedDate}</strong>
              </div>
              <div className="ride-summary-field">
                <span>{t('TIME')}</span>
                <strong>{tripData?.formattedTime}</strong>
              </div>
            </div>
          </aside>
        )}

        {/* Vehicle List */}
        {loading ? (
          <div className="loading-state">
            <p>{t('Loading vehicles...')}</p>
          </div>
        ) : error && vehicles.length === 0 ? (
          <div className="error-state ride-list-panel">
            <p>{t(error)}</p>
          </div>
        ) : (
          <div className="vehicle-list ride-list-panel">
            {vehicles.map((car) => {
              const packages = Array.isArray(car.packages) ? car.packages : [];
              const isExpanded = expandedCars.has(car.carId);
              const carName = car.carMakeName && car.carModelName
                ? `${car.carMakeName} ${car.carModelName}`
                : car.carMakeName || car.carModelName || 'Car';
              
              return (
                <div key={car.carId} className="car-group">
                  {/* Car Header */}
                  <div 
                    className="car-header"
                    onClick={() => toggleCarExpansion(car.carId)}
                  >
                    <div className="car-header-info">
                      <div className="car-image-container">
                        {car.carImage ? (
                          <img
                            src={car.carImage}
                            alt={carName}
                            className="car-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <img
                          src={process.env.PUBLIC_URL + '/images/car.svg'}
                          alt=""
                          className="car-image car-image-fallback"
                          style={{ display: car.carImage ? 'none' : 'block' }}
                        />
                      </div>
                      <div className="car-header-details">
                        <h3 className="car-name">{carName}</h3>
                        <div className="car-info-row">
                          <p className="car-package-count">{packages.length} {packages.length === 1 ? 'Package' : 'Packages'} Available</p>
                          {(car.seatingCapacity > 0 || car.luggageCapacity > 0) && (
                            <div className="car-capacity-info">
                              {car.seatingCapacity > 0 && (
                                <span className="car-capacity-item">
                                  <svg className="capacity-icon-small" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H11C9.93913 15 8.92172 15.4214 8.17157 16.1716C7.42143 16.9217 7 17.9391 7 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  {car.seatingCapacity}
                                </span>
                              )}
                              {car.luggageCapacity > 0 && (
                                <span className="car-capacity-item">
                                  <svg className="capacity-icon-small" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M3 6H21" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  {car.luggageCapacity}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="car-expand-icon">
                      <svg 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
                      >
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>

                  {/* Packages List (shown when expanded) */}
                  {isExpanded && (
                    <div className="packages-list">
                      {packages.map((pkg, pkgIndex) => {
                        const resolvedPackageId = resolvePackageId(pkg);
                        const packageId = resolvedPackageId || `${car.carId}_${pkgIndex}`;
                        const packageName = pkg.packageName || pkg.categoryName || pkg.name || 'Package';
                        const packageImage = pkg.imageUrl || pkg.image || null;
                        const passengerCapacity = pkg.seatingCapacity || pkg.passengerCapacity || pkg.passengers || pkg.capacity || 0;
                        const luggageCapacity = pkg.luggageCapacity || pkg.bags || pkg.luggage || 0;
                        const price = pkg.estimatedTotalCost || pkg.basePrice || pkg.maxFare || pkg.minFare || 0;
                        const distanceText = pkg.distanceText || null;
                        const durationText = pkg.durationText || null;
                        const isRecommended = pkg.recommended || false;

                        // Use a unique identifier for comparison
                        const selectedVehicleId = selectedVehicle?.clientPackageId || selectedVehicle?.packageId || selectedVehicle?.id;
                        const currentPackageId = pkg.clientPackageId || pkg.packageId || packageId;
                        const isSelected = selectedVehicle && selectedVehicleId && currentPackageId && selectedVehicleId === currentPackageId;

                        return (
                          <div
                            key={packageId}
                            className={`vehicle-card package-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleVehicleSelect({
                              ...pkg,
                              index: pkgIndex,
                              id: resolvedPackageId || packageId,
                              clientPackageId: resolvedPackageId,
                              packageId: pkg.packageId || resolvedPackageId,
                              carId: pkg.carId || car.carId,
                              carMakeName: pkg.carMakeName || car.carMakeName,
                              carModelName: pkg.carModelName || car.carModelName,
                              imageUrl: pkg.imageUrl || pkg.image || car.carImage || null
                            })}
                          >
                            <div className="vehicle-image-container">
                              {packageImage ? (
                                <img
                                  src={packageImage}
                                  alt={packageName}
                                  className="vehicle-image"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="vehicle-image-placeholder" style={{ display: packageImage ? 'none' : 'flex' }}>
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 17H19L17 9H7L5 17ZM7 7L9 3H15L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <circle cx="7" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
                                  <circle cx="17" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              </div>
                            </div>
                            
                            <div className="vehicle-details">
                              <div className="vehicle-name-row">
                                <h3 className="vehicle-name">{packageName}</h3>
                                {isRecommended && (
                                  <span className="recommended-badge">{t('Recommended')}</span>
                                )}
                              </div>
                              {(distanceText || durationText) && (
                                <p className="vehicle-trip-info">
                                  {distanceText && <span>{distanceText}</span>}
                                  {distanceText && durationText && <span> • </span>}
                                  {durationText && <span>{durationText}</span>}
                                </p>
                              )}
                              <div className="vehicle-capacity">
                                <div className="capacity-item">
                                  <svg className="capacity-icon passenger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H11C9.93913 15 8.92172 15.4214 8.17157 16.1716C7.42143 16.9217 7 17.9391 7 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  <span>{passengerCapacity}</span>
                                </div>
                                <div className="capacity-item">
                                  <svg className="capacity-icon luggage-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M3 6H21" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  <span>{luggageCapacity}</span>
                                </div>
                              </div>
                            </div>

                            <div className="vehicle-price">
                              <span className="price-amount">₹ {price.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Continue Button */}
        {!loading && vehicles.length > 0 && (
          <button
            className={`continue-btn ${selectedVehicle ? 'active' : ''}`}
            onClick={handleContinue}
            disabled={!selectedVehicle}
          >
            Go to Fare Breakup
          </button>
        )}
      </div>
    </div>
  );
};

export default VehicleSelection;
